import PERMISSIONS_CONSTANTS from '@/constants/permission-constants';
import { useOrganizationStore } from '@/stores/organization.store';
import { useProjectsStore } from '@/stores/projects.store';
import { Popover, Transition } from '@headlessui/react';
import { useSignOut, useUserData } from '@nhost/nextjs';
import { useRouter } from 'next/router';
import { Fragment, useEffect, useState } from 'react';
import { shallow } from 'zustand/shallow';
import CreateProjectPopupComponent from '../projects/create-project-popup.component';
import PlanAndBillingPopupComponent from '../utils/plan-and-billing-popup.component';
import FeedbackSurveyPopupComponent from './feedback-survey-popup.component';
import axios from 'axios';
import CommonConstants from '@/constants/common-constants';

export default function DashboardOrganizationDropdown() {
  // Variables
  const router = useRouter();
  const userData = useUserData();
  const {
    selectedOrganization,
    organizations,
    setSelectedOrganization,
    organizationPermissions
  } = useOrganizationStore();
  const [resetProjects, projects] = useProjectsStore(
    s => [s.resetProjects, s.projects],
    shallow
  );

  // Nhost hooks
  const { signOut } = useSignOut();

  const handleLogout = async () => {
    const gptToken = localStorage.getItem('gpt_access_token');

    if (gptToken) {
      try {
        await axios.post(CommonConstants.API_PREFIX + '/oauth/revoke', {
          token: gptToken,
          client_id: CommonConstants.OAUTH_CLIENT_ID,
          client_secret: CommonConstants.OAUTH_CLIENT_SECRET
        });
      } catch (e) {
        console.warn('Failed to revoke GPT token:', e);
      }
      localStorage.removeItem('gpt_access_token');
    }

    await signOut();
  };

  // States
  const [isFeedbackSurveyPopupOpen, setIsFeedbackSurveyPopupOpen] =
    useState<boolean>(false);
  const [isCreateProjectPopupOpen, setIsCreateProjectPopupOpen] =
    useState<boolean>(false);
  const [showUpgrade, setShowUpgrade] = useState<boolean>(false);
  const [isPlanAndBillingPopupOpen, setIsPlanAndBillingPopupOpen] =
    useState<boolean>(false);

  useEffect(() => {
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      const script = document.createElement('script');

      script.text = `(function(C, A, L) {
          let p = function(a, ar) {
              a.q.push(ar);
          };

          let d = C.document;
          C.Cal = C.Cal || function() {
              let cal = C.Cal;
              let ar = arguments;
              if (!cal.loaded) {
                  cal.ns = {};
                  cal.q = cal.q || [];
                  d.head.appendChild(d.createElement("script")).src = A;
                  cal.loaded = true;
              }
              if (ar[0] === L) {
                  const api = function() {
                      p(api, arguments);
                  };
                  const namespace = ar[1];
                  api.q = api.q || [];
                  typeof namespace === "string" ? (cal.ns[namespace] = api) && p(api, ar) : p(cal, ar);
                  return;
              }
              p(cal, ar);
          };
        })(window, "https://app.cal.com/embed/embed.js", "init");
        
        Cal("init", {
            origin: "https://app.cal.com"
        });`;

      document.body.appendChild(script);

      // Cleanup on component unmount
      return () => {
        document.body.removeChild(script);
      };
    }
  }, []);

  useEffect(() => {
    if (projects.length > 0) {
      checkProjectLimitation();
    }
  }, [projects]);

  function checkProjectLimitation() {
    if (
      selectedOrganization?.pricing_subscription?.pricing_plan
        ?.pricing_plan_type?.is_freeplan &&
      selectedOrganization?.pricing_subscription?.pricing_plan
        ?.pricing_limitations &&
      selectedOrganization?.pricing_subscription?.pricing_plan
        ?.pricing_limitations.length > 0
    ) {
      const runChecklistLimitation =
        selectedOrganization?.pricing_subscription?.pricing_plan?.pricing_limitations.find(
          limitation => limitation.type === 'projects'
        );
      if (
        runChecklistLimitation &&
        runChecklistLimitation?.value &&
        projects.length &&
        projects.length >= parseInt(runChecklistLimitation?.value)
      ) {
        return setShowUpgrade(true);
      } else {
        return setShowUpgrade(false);
      }
    }
  }

  return (
    <div className="w-full p-2 pb-0">
      <Popover className="w-full relative inline-block text-left text-sm">
        {({ close }) => (
          <>
            <Popover.Button
              className="group inline-flex w-full items-center gap-2 rounded-full border-[1.5px] border-gray-300 
              pl-1 pr-2 py-1.5
              hover:bg-slate-100
              focus-visible:ring-0
              focus-visible:ring-white 
              focus-visible:ring-opacity-75"
            >
              {/* Org image */}
              <div className="pl-1">
                {/* <img
              className="w-7 h-7 rounded-full mx-auto bg-slate-300"
              src={userData?.avatarUrl}
              alt=""
              width="384"
              height="512"
            /> */}

                {/* First letter */}
                <div className="w-8 h-8 text-lg flex items-center justify-center rounded-full bg-gray-200 text-gray-600 uppercase">
                  {selectedOrganization?.name?.charAt(0)}
                </div>
              </div>

              {/* Org name */}
              <div className="overflow-hidden">
                <div className="text-left truncate w-[96%]">
                  {selectedOrganization?.name}
                </div>
                <div className="text-left text-xs truncate opacity-60">
                  {
                    selectedOrganization?.pricing_subscription?.pricing_plan
                      ?.pricing_plan_type?.name
                  }
                </div>
              </div>

              {/* Dropdown arrow */}
              <div className="ml-auto flex w-5 h-5 items-center justify-center rounded-full hover:bg-slate-100">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 transition-transform duration-200
                    opacity-70
                    group-hover:opacity-100
                    group-aria-expanded:rotate-180
                  `}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.75}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </Popover.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Popover.Panel
                className="absolute left-2 mt-2 w-56 origin-top-right 
                divide-y divide-gray-100 rounded-lg bg-white shadow-lg border-[1.5px] border-gray-200
                ring-0 ring-white ring-opacity-5 focus:outline-none z-10 max-h-[80vh] overflow-auto"
              >
                <div className="divide-y divide-gray-200">
                  {/* Email */}
                  {userData && userData?.email && (
                    <>
                      <div className="flex flex-col px-2">
                        {userData?.displayName && (
                          <span className="opacity-80 pb-0 p-2">
                            {userData?.displayName}
                          </span>
                        )}
                        <span className="opacity-50 text-xs pt-0 p-2">
                          {userData?.email}
                        </span>
                      </div>
                    </>
                  )}

                  {/* List of organization */}
                  {organizations && organizations.length > 0 && (
                    <>
                      <div className="flex gap-1 flex-col">
                        <span className="opacity-50 uppercase px-3.5 pt-3 text-xs">
                          organizations
                        </span>
                        {organizations.map((organization, index) => {
                          const isSelected =
                            organization.id === selectedOrganization?.id;
                          return (
                            <button
                              key={index}
                              className="text-left mx-1 rounded-md px-2 py-2 hover:bg-slate-100 cursor-pointer"
                              onClick={() => {
                                if (
                                  organization?.id &&
                                  selectedOrganization?.id !== organization?.id
                                ) {
                                  resetProjects();
                                  setSelectedOrganization(organization?.id);
                                  router.push('/projects');
                                  close();
                                }
                              }}
                            >
                              <div className="flex items-center gap-2">
                                {/* Logo */}
                                <div>
                                  {/* First letter */}
                                  <div className="w-8 h-8 text-lg flex items-center justify-center rounded-2xl bg-gray-200 text-gray-600 uppercase">
                                    {organization?.name?.charAt(0)}
                                  </div>
                                </div>

                                {/* Details */}
                                <div className="flex-1">
                                  <div className="line-clamp-1">
                                    {organization?.name}
                                  </div>
                                  <div className="text-xs opacity-60 line-clamp-1">
                                    {
                                      organization?.pricing_subscription
                                        ?.pricing_plan?.pricing_plan_type?.name
                                    }
                                  </div>
                                </div>

                                {/* Selected tick */}
                                <div className="w-4 h-full items-start">
                                  {isSelected && (
                                    <svg viewBox="0 0 14 14">
                                      <polygon points="5.5 11.9993304 14 3.49933039 12.5 2 5.5 8.99933039 1.5 4.9968652 0 6.49933039" />
                                    </svg>
                                  )}
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>

                <div className="px-1 py-1">
                  {organizationPermissions[
                    PERMISSIONS_CONSTANTS.MANAGE_BILLING
                  ] && (
                    // <div>
                    //   <>
                    //   {({ active }: {active: boolean}) => (
                    <button
                      className={` group flex w-full items-center font-normal rounded-md px-2 py-2 text-black/60 hover:text-black hover:bg-slate-100`}
                      onClick={() => {
                        router.push('/settings/plan-billing');
                        close();
                      }}
                    >
                      Billing
                    </button>
                    //   )}</>
                    // </div>
                  )}

                  {/* <div><>
                {({ active }: {active: boolean}) => ( */}
                  <button
                    className={` group flex w-full items-center font-normal rounded-md px-2 py-2 text-black/[.6] hover:text-black hover:bg-slate-100`}
                    onClick={() => {
                      router.push('/settings/members');
                      close();
                    }}
                  >
                    Organization Members
                  </button>
                  {/* )}</>
              </div> */}

                  {/* New project */}
                  {organizationPermissions?.[
                    PERMISSIONS_CONSTANTS.CREATE_PROJECT
                  ] &&
                    (showUpgrade ? (
                      // <CLTooltip content="Upgrade To Create Project">
                      // <div>
                      //   <>
                      //   {({ active }: {active: boolean}) => (
                      <button
                        className={` group flex w-full items-center font-normal text-black/[.6] hover:text-black rounded-md px-2 py-2 hover:bg-slate-100`}
                        onClick={() => {
                          setIsPlanAndBillingPopupOpen(true);
                          close();
                        }}
                      >
                        New Project
                      </button>
                    ) : (
                      //   )}</>
                      // </div>
                      // </CLTooltip>
                      // <div><>
                      //   {({ active }: {active: boolean}) => (
                      <button
                        className={` group flex w-full items-center font-normal text-black/[.6] hover:text-black rounded-md px-2 py-2 hover:bg-slate-100`}
                        onClick={() => {
                          setIsCreateProjectPopupOpen(true);
                          close();
                        }}
                      >
                        New Project
                      </button>
                      //   )}</>
                      // </div>
                    ))}
                </div>

                <div className="px-1 py-1">
                  {/* <div><>
                {({ active }: {active: boolean}) => ( */}
                  {/* <button
                    className={` group flex w-full items-center font-normal text-black/[.6] hover:text-black rounded-md px-2 py-2 hover:bg-slate-100`}
                    onClick={() => {
                      setIsFeedbackSurveyPopupOpen(true);
                      close();
                    }}
                  >
                    Feedback
                  </button> */}
                  {/* )}</>
              </div> */}

                  {/* <div><>
                {({ active }: {active: boolean}) => ( */}
                  <button
                    className={` group flex w-full items-center font-normal text-black/[.6] hover:text-black rounded-md px-2 py-2 hover:bg-slate-100`}
                    data-cal-link="checklist/book-a-demo"
                  >
                    Book a demo
                  </button>
                  {/* )}</>
              </div> */}

                  {/* <div><>
                {({ active }: {active: boolean}) => ( */}
                  {/* <a
                    href="https://app.loopedin.io/checklistgg"
                    target={'_blank'}
                    rel="noreferrer"
                    className={` group flex w-full items-center font-normal text-black/[.6] hover:text-black rounded-md px-2 py-2 hover:bg-slate-100`}
                  >
                    Roadmap ↗
                  </a> */}
                  {/* )}</>
              </div> */}

                  {/* <div><>
                {({ active }: {active: boolean}) => ( */}
                  <button
                    className={` group flex w-full items-center font-normal text-black/[.6] hover:text-black rounded-md px-2 py-2 hover:bg-slate-100`}
                    onClick={handleLogout}
                  >
                    Sign out
                  </button>
                  {/* )}</>
              </div> */}
                </div>
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>

      <FeedbackSurveyPopupComponent
        isOpen={isFeedbackSurveyPopupOpen}
        setOpen={setIsFeedbackSurveyPopupOpen}
        orgId={selectedOrganization?.id}
        email={userData?.email}
      ></FeedbackSurveyPopupComponent>

      <CreateProjectPopupComponent
        isOpen={isCreateProjectPopupOpen}
        setIsOpen={setIsCreateProjectPopupOpen}
      />

      <PlanAndBillingPopupComponent
        isOpen={isPlanAndBillingPopupOpen}
        setIsOpen={setIsPlanAndBillingPopupOpen}
      ></PlanAndBillingPopupComponent>
    </div>
  );
}
